import React from 'react';
import { ArrowRight, MapPin, Users, RefreshCw, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 lg:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16 lg:pb-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
              Discover Local
              <span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent block sm:inline">
                {" "}Treasures
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
              Find amazing garage sales, estate sales, and curb alerts near you. 
              Turn your weekend into an adventure of discovery!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center px-4 max-w-lg sm:max-w-none mx-auto">
              <button 
                onClick={() => navigate('/listings')}
                className="group bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm lg:text-base"
              >
                Start Exploring
                <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex gap-2">
                <button 
                  onClick={() => navigate('/listings')}
                  className="bg-white hover:bg-gray-50 text-gray-900 px-4 lg:px-6 py-3 lg:py-4 rounded-full font-semibold border border-gray-200 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 text-sm lg:text-base flex items-center gap-2"
                  title="View sales in list format"
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
                <button 
                  onClick={() => navigate('/map')}
                  className="bg-white hover:bg-gray-50 text-gray-900 px-4 lg:px-6 py-3 lg:py-4 rounded-full font-semibold border border-gray-200 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 text-sm lg:text-base flex items-center gap-2"
                  title="View sales on interactive map"
                >
                  <MapPin className="w-4 h-4" />
                  <span className="hidden sm:inline">Map</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated Background Elements - Responsive */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-1/4 left-1/4 w-32 lg:w-64 h-32 lg:h-64 bg-amber-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-32 lg:w-64 h-32 lg:h-64 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-32 lg:w-64 h-32 lg:h-64 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
      </section>

      {/* Features Section - Responsive Grid */}
      <section className="py-12 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Treasure Hunters Love HollerFinds
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to discover amazing local finds
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: <MapPin className="w-6 lg:w-8 h-6 lg:h-8" />,
                title: "Interactive Map",
                description: "See all garage sales, estate sales, and curb alerts on an easy-to-use map. Filter by type, date, and distance."
              },
              {
                icon: <RefreshCw className="w-6 lg:w-8 h-6 lg:h-8" />,
                title: "Fresh Listings",
                description: "We automatically pull new garage sales, estate sales, and curb alerts from across the web so you don't have to."
              },
              {
                icon: <Users className="w-6 lg:w-8 h-6 lg:h-8" />,
                title: "Post Your Own",
                description: "Hosting a sale? List it for free and reach treasure hunters in your area."
              }
            ].map((feature, index) => (
              <div key={index} className="group p-6 lg:p-8 rounded-2xl bg-white border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="text-amber-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section - Clickable */}
      <section className="py-12 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4">
              What You'll Discover
            </h2>
            <p className="text-lg lg:text-xl text-gray-600">
              From vintage treasures to everyday essentials
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {[
              {
                title: "Garage Sales",
                description: "Family treasures at unbeatable prices",
                image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop",
                color: "from-blue-500 to-purple-600",
                category: "garage"
              },
              {
                title: "Estate Sales",
                description: "Quality antiques and collectibles",
                image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=250&fit=crop",
                color: "from-amber-500 to-orange-600",
                category: "estate"
              },
              {
                title: "Curb Alerts",
                description: "Free finds and unexpected gems",
                image: "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=250&fit=crop",
                color: "from-green-500 to-teal-600",
                category: "curb"
              }
            ].map((category, index) => (
              <button
                key={index}
                onClick={() => navigate(`/categories?type=${category.category}`)}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full text-left"
              >
                <img 
                  src={category.image} 
                  alt={category.title}
                  className="w-full h-48 lg:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-80`}></div>
                <div className="absolute inset-0 p-4 lg:p-6 flex flex-col justify-end text-white">
                  <h3 className="text-xl lg:text-2xl font-bold mb-2">{category.title}</h3>
                  <p className="text-sm opacity-90">{category.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Responsive buttons */}
      <section className="py-12 lg:py-24 bg-gradient-to-r from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl lg:text-4xl font-bold text-white mb-4 lg:mb-6">
            Ready for Your Next Adventure?
          </h2>
          <p className="text-lg lg:text-xl text-amber-100 mb-6 lg:mb-8">
            Browse local garage sales, estate sales, and curb alerts on the map &mdash; or jump to the list view.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center max-w-md sm:max-w-none mx-auto">
            <button
              onClick={() => navigate('/listings')}
              className="bg-white hover:bg-gray-50 text-amber-600 px-6 lg:px-8 py-3 lg:py-4 rounded-full font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm lg:text-base"
            >
              Start Exploring Now
            </button>
            <button
              onClick={() => navigate('/map')}
              className="border-2 border-white text-white hover:bg-white hover:text-amber-600 px-6 lg:px-8 py-3 lg:py-4 rounded-full font-semibold transition-all duration-300 text-sm lg:text-base"
            >
              Open Map
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}; 